import React, { useState, useEffect, useRef } from 'react';
import { Grid, Paper, Typography, Box, IconButton, Select, MenuItem, FormControl, InputLabel, Button, Snackbar, Alert, Portal, Tooltip } from '@mui/material';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Modal from '../modal';
import { TimeSlotName } from '../../../services/treatmentAdministration';
import { getTimeSlotsForFrequency, isDayActiveForFrequency } from '../../../utils/frequencyConfig';
import {
    formatDateToISO,
    getWeekStart,
    getWeekEnd,
    getWeeksInMonth,
    navigateWeek,
    getMondayBasedDayOfWeek
} from '../../../utils/dateUtils';
import { useSnackBarState, useResearcher } from '../../../hooks';
import { researcherFullName, dateAndTimeFromPostgresString } from '../../../utils';
import { createTranslateWithFallback } from '../../../utils/translate';

interface TreatmentInpatientChartViewProps extends LocalizeContextProps {
    data: Array<{
        drug: string;
        quantity: number;
        doseAmount: string;
        doseUnit: string;
        frequency: string;
        route: string;
        stopped: string;
        treatmentPostId?: number;
        startDate?: Date;
        endDate?: Date;
        stoppedDate?: Date;
        administrations: { [dateKey: string]: {
            timeSlots: { [slotName: string]: {
                status: "successful" | "not successful" | null;
                administrationId?: number;
                administeredBy?: string;
                createdAt?: string;
                reasonNotAdministered?: string; 
            }};
        }};
    }>;
    patientUuid?: string;
    uuidInvestigation?: string;
    language?: string;
    onStatusUpdate?: (drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, status: "successful" | "not successful" | null, reasonNotAdministered?: string) => void;
    onWeekChange?: (weekStartDate: Date) => void;
}

const TreatmentInpatientChartView: React.FC<TreatmentInpatientChartViewProps> = ({ 
    data: initialData, 
    patientUuid, 
    uuidInvestigation,
    language,
    translate,
    onStatusUpdate,
    onWeekChange 
}) => {
    // Create translate function with fallback support
    const translateWithFallback = createTranslateWithFallback(translate);
    
    const [data, setData] = useState(initialData);
    const daysInChart = 7;

    // Add state for week navigation
    const [currentDate, setCurrentDate] = useState(new Date());
    const [weekStartDate, setWeekStartDate] = useState<Date>(new Date());

    // Add state for week selector modal
    const [weekSelectorOpen, setWeekSelectorOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

    const isInitialMount = useRef(true);

    // Update local data when initialData changes
    useEffect(() => {
        setData(initialData);
    }, [initialData]);

    // Initialize week start date on mount (Monday-based week)
    useEffect(() => {
        const today = new Date();
        const startOfWeek = getWeekStart(today, 'monday');
        
        setWeekStartDate(startOfWeek);
        setCurrentDate(today);
        
        // Set flag to false after a small delay to allow parent to load initial data
        const timeoutId = setTimeout(() => {
            isInitialMount.current = false;
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, []);

    // Add useEffect to watch weekStartDate changes
    useEffect(() => {
        // Skip calling onWeekChange on initial mount - parent handles initial load
        if (isInitialMount.current) {
            return;
        }

        onWeekChange?.(weekStartDate);
    }, [weekStartDate, onWeekChange]);

    // getWeeksInMonth is now imported from dateUtils

    // Format week range for display
    const formatWeekRange = (weekStart: Date, locale: string = 'en'): string => {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        const startDay = weekStart.getDate();
        const endDay = weekEnd.getDate();
        const startMonth = weekStart.toLocaleString(locale, { month: 'short' });
        const endMonth = weekEnd.toLocaleString(locale, { month: 'short' });
        
        if (startMonth === endMonth) {
            return `${startDay}-${endDay} ${startMonth}`;
        } else {
            return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
        }
    };

    // Open week selector modal
    const openWeekSelector = () => {
        // Initialize with current week's year and month
        const currentYear = weekStartDate.getFullYear();
        const currentMonth = weekStartDate.getMonth();
        
        // Ensure selected year has active treatments, otherwise find the first valid year
        const filteredYears = getFilteredYears();
        let initialYear = currentYear;
        if (!filteredYears.includes(currentYear)) {
            initialYear = filteredYears.length > 0 ? filteredYears[0] : currentYear;
        }
        setSelectedYear(initialYear);
        
        // Ensure selected month has active treatments for the selected year, otherwise find the first valid month
        const filteredMonths = getFilteredMonths(initialYear);
        let initialMonth = currentMonth;
        if (!filteredMonths.includes(currentMonth) || initialYear !== currentYear) {
            initialMonth = filteredMonths.length > 0 ? filteredMonths[0] : currentMonth;
        }
        setSelectedMonth(initialMonth);
        
        // Find which week index in that month
        const filteredWeeks = getFilteredWeeks(initialYear, initialMonth);
        const currentWeekIndex = filteredWeeks.findIndex(
            week => week.getTime() === weekStartDate.getTime()
        );
        setSelectedWeekIndex(currentWeekIndex >= 0 ? currentWeekIndex : 0);
        
        setWeekSelectorOpen(true);
    };

    // Apply selected week
    const applyWeekSelection = () => {
        const filteredWeeks = getFilteredWeeks(selectedYear, selectedMonth);
        if (filteredWeeks[selectedWeekIndex]) {
            const selectedWeek = filteredWeeks[selectedWeekIndex];
            setWeekStartDate(selectedWeek);
        }
        setWeekSelectorOpen(false);
    };

    // Calculate days of the current week
    const getWeekDays = () => {
        const days = [];
        for (let i = 0; i < daysInChart; i++) {
            const day = new Date(weekStartDate);
            day.setDate(weekStartDate.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const weekDays = getWeekDays();
    const locale = language === 'es' ? 'es' : language === 'fr' ? 'fr' : 'en';
    const currentMonth = weekStartDate.toLocaleString(locale, { month: 'long', year: 'numeric' });
    
    // Calculate current week number and range for display
    const getCurrentWeekInfo = () => {
        const weeksInMonth = getWeeksInMonth(weekStartDate.getFullYear(), weekStartDate.getMonth());
        const currentWeekIndex = weeksInMonth.findIndex(
            week => week.getTime() === weekStartDate.getTime()
        );
        const weekNumber = currentWeekIndex >= 0 ? currentWeekIndex + 1 : 1;
        const weekRange = formatWeekRange(weekStartDate, locale); // Pass locale here
        return `${translate('hospital.treatment-inpatient-week')} ${weekNumber}: ${weekRange}`;
    };
    
    const currentWeekInfo = getCurrentWeekInfo();

    // Helper function to get the current week start (today's week)
    const getCurrentWeekStart = (): Date => {
        const today = new Date();
        const currentWeekStart = getWeekStart(today, 'monday');
        currentWeekStart.setHours(0, 0, 0, 0);
        return currentWeekStart;
    };

    // Helper function to check if a week is in the future
    const isWeekInFuture = (weekStart: Date): boolean => {
        const weekStartDate = new Date(weekStart);
        weekStartDate.setHours(0, 0, 0, 0);
        const currentWeekStart = getCurrentWeekStart();
        return weekStartDate > currentWeekStart;
    };

    // Helper function to check if any treatment is active during a week
    const hasTreatmentActiveInWeek = (weekStart: Date): boolean => {
        const weekEnd = getWeekEnd(weekStart);
        const weekStartCopy = new Date(weekStart);
        const weekEndCopy = new Date(weekEnd);
        weekStartCopy.setHours(0, 0, 0, 0);
        weekEndCopy.setHours(0, 0, 0, 0);
        
        return data.some(treatment => {
            // Determine the effective end date (stoppedDate takes precedence over endDate)
            const effectiveEndDate = treatment.stoppedDate || treatment.endDate;
            
            // Check if treatment is active during this week
            // Treatment is active if:
            // 1. It has started (startDate exists and is <= weekEnd)
            // 2. It hasn't ended yet (no effectiveEndDate OR effectiveEndDate >= weekStart)
            
            if (!treatment.startDate) {
                return false; // Treatment must have a start date
            }
            
            const startDate = new Date(treatment.startDate);
            startDate.setHours(0, 0, 0, 0);
            
            // Treatment hasn't started yet
            if (startDate > weekEndCopy) {
                return false;
            }
            
            // Treatment has started, check if it's still active
            if (effectiveEndDate) {
                const endDate = new Date(effectiveEndDate);
                endDate.setHours(0, 0, 0, 0);
                // Treatment is active if endDate >= weekStart (overlaps with week)
                return endDate >= weekStartCopy;
            } else {
                // No end date - treatment is ongoing, so it's active if it has started
                return true;
            }
            
        });
    };

    // Helper function to check if a month has any active treatments
    const hasActiveTreatmentsInMonth = (year: number, month: number): boolean => {
        const weeksInMonth = getWeeksInMonth(year, month);
        return weeksInMonth.some(weekStart => hasTreatmentActiveInWeek(weekStart));
    };

    // Helper function to check if a year has any active treatments
    const hasActiveTreatmentsInYear = (year: number): boolean => {
        // Check all 12 months in the year
        for (let month = 0; month < 12; month++) {
            if (hasActiveTreatmentsInMonth(year, month)) {
                return true;
            }
        }
        return false;
    };

    // Get filtered years that have active treatments
    const getFilteredYears = (): number[] => {
        return Array.from({ length: 201 }, (_, i) => 1900 + i)
            .filter(year => hasActiveTreatmentsInYear(year));
    };

    // Get filtered months for a given year that have active treatments
    const getFilteredMonths = (year: number): number[] => {
        return Array.from({ length: 12 }, (_, i) => i)
            .filter(month => hasActiveTreatmentsInMonth(year, month));
    };

    // Get filtered weeks for a given month that have active treatments
    const getFilteredWeeks = (year: number, month: number): Date[] => {
        const weeksInMonth = getWeeksInMonth(year, month);
        return weeksInMonth.filter(weekStart => hasTreatmentActiveInWeek(weekStart));
    };

    // Navigation functions - Allow navigation based on treatment dates
    const goToPreviousWeek = () => {
        const previousWeek = navigateWeek(weekStartDate, 'prev');
        // Check if week is disabled
        if (isWeekDisabled(previousWeek)) {
            setShowSnackbar({
                show: true,
                message: translate('hospital.treatment-inpatient-no-treatments-previous-week').toString(),
                severity: 'warning'
            });
            return;
        }
        // Only navigate if the week is not disabled
        setWeekStartDate(previousWeek);
    };

    const goToNextWeek = () => {
        const nextWeek = navigateWeek(weekStartDate, 'next');
        // Check if week is disabled
        if (isWeekDisabled(nextWeek)) {
            setShowSnackbar({
                show: true,
                message: translate('hospital.treatment-inpatient-no-treatments-next-week').toString(),
                severity: 'warning'
            });
            return;
        }
        // Only navigate if the week is not disabled
        setWeekStartDate(nextWeek);
    };

    const isWeekDisabled = (weekStart: Date): boolean => {
        const isFuture = isWeekInFuture(weekStart);
        const currentWeekStart = getCurrentWeekStart();
        const isPast = weekStart < currentWeekStart;
        
        // Disable if it's in the future AND there's no treatment active in that week
        // Also disable if it's in the past AND there's no treatment active in that week
        return (isFuture && !hasTreatmentActiveInWeek(weekStart)) || 
               (isPast && !hasTreatmentActiveInWeek(weekStart));
    };

    // Check if next week navigation is disabled
    const isNextWeekDisabled = (): boolean => {
        const nextWeek = navigateWeek(weekStartDate, 'next');
        // Disable if it's in the future AND there's no treatment active in that week
        return isWeekDisabled(nextWeek);
    };

    // Check if previous week navigation is disabled
    const isPreviousWeekDisabled = (): boolean => {
        const previousWeek = navigateWeek(weekStartDate, 'prev');
        // Disable if there's no treatment active in the previous week
        return isWeekDisabled(previousWeek);
    };

    // Filter treatments based on selected week
    const getFilteredData = () => {
        if (!weekStartDate) {
            return data;
        }

        // Calculate the Monday of the selected week
        const selectedWeekMonday = getWeekStart(weekStartDate, 'monday');
        selectedWeekMonday.setHours(0, 0, 0, 0);

        return data.filter((treatment, originalIndex) => {
            // Condition 1: Calculate the Monday of the week of the start date
            // If this Monday >= Monday of selected week, hide it
            if (treatment.startDate) {
                const startDateMonday = getWeekStart(treatment.startDate, 'monday');
                startDateMonday.setHours(0, 0, 0, 0);
                
                if (startDateMonday > selectedWeekMonday) {
                    return false; // Hide - treatment hasn't started yet
                }
            }

            // Condition 2: Calculate the Sunday of the week of the end date or stopped date
            // If this Sunday < Monday of selected week, hide it. Otherwise don't hide it
            const referenceDate = treatment.stoppedDate || treatment.endDate;
            
            if (referenceDate) {
                // Get the Sunday of the week containing the reference date
                const referenceDateMonday = getWeekStart(referenceDate, 'monday');
                const referenceDateSunday = getWeekEnd(referenceDateMonday);
                referenceDateSunday.setHours(0, 0, 0, 0);
                
                // If Sunday of reference date week < Monday of selected week, hide it
                if (referenceDateSunday < selectedWeekMonday) {
                    return false; // Hide - treatment ended/stopped before selected week
                }
            }

            return true;
        });
    };

    const filteredData = getFilteredData();

    // Helper function to check if a day is valid for a treatment
    const isDayValidForTreatment = (day: Date, treatment: typeof data[number]): boolean => {
        const dayDate = new Date(day);
        dayDate.setHours(0, 0, 0, 0);

        // Check if day is before start date
        if (treatment.startDate) {
            const startDate = new Date(treatment.startDate);
            startDate.setHours(0, 0, 0, 0);
            if (dayDate < startDate) {
                return false; // Hide - day is before treatment started
            }
        }

        // Check if day is after end/stopped date
        const referenceDate = treatment.stoppedDate || treatment.endDate;
        if (referenceDate) {
            const endDate = new Date(referenceDate);
            endDate.setHours(0, 0, 0, 0);
            if (dayDate > endDate) {
                return false; // Hide - day is after treatment ended/stopped
            }
        }

        return true; // Day is valid
    };

    const [popupOpen, setPopupOpen] = useState(false);                      //For the popup that appears after clicking "Provide reason for not administering the drug" 
    const [selectedCell, setSelectedCell] = useState<{
        drugIndex: number;
        day: string;
        timeSlot: TimeSlotName;
    } | null>(null);
    const [selectedReason, setSelectedReason] = useState<string>('');

    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    const notAdministeringReasons = [
        { code: 'A', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-absent') },
        { code: 'F', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-fasting') },
        { code: 'L', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-on-leave') },
        { code: 'N', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-not-available') },
        { code: 'R', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-refused') },
        { code: 'S', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-self-administered') },
        { code: 'V', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-vomiting') },
        { code: 'W', reasonNotAdministered: translate('hospital.treatment-inpatient-reason-withheld') }
    ];

    // Add new state for context menu
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
    const [contextMenuCell, setContextMenuCell] = useState<{
        drugIndex: number;
        day: string; // Changed from number to string
        timeSlot: TimeSlotName;
    } | null>(null);

    // Add a new state for the cell action menu (for non-empty cells), i.e. the menu that says "Mark as ✓" or "Mark as ✗"
    const [cellActionMenuOpen, setCellActionMenuOpen] = useState(false);
    const [cellActionMenuPosition, setCellActionMenuPosition] = useState<{ x: number; y: number } | null>(null);
    const [cellActionMenuCell, setCellActionMenuCell] = useState<{
        drugIndex: number;
        day: string; // Changed from number to string
        timeSlot: TimeSlotName;
        currentStatus: "successful" | "not successful";
    } | null>(null);


    // Validation function to check if date is editable (today or yesterday only)
    const isDateEditable = (dateKey: string): boolean => {
        const cellDate = new Date(dateKey);
        cellDate.setHours(0, 0, 0, 0); // Start of the cell's day
        
        const now = new Date();
        const today = new Date(now);
        today.setHours(0, 0, 0, 0); // Start of today
        
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1); // Start of yesterday
        
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1); // Start of tomorrow
        
        // Allow only today and yesterday
        return cellDate >= yesterday && cellDate < tomorrow;
    };

    const isFutureDate = (dateKey: string): boolean => {
        const cellDate = new Date(dateKey);
        cellDate.setHours(0, 0, 0, 0);
        
        const now = new Date();
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
        return cellDate >= tomorrow;
    };

    const isPastDate = (dateKey: string): boolean => {
        const cellDate = new Date(dateKey);
        cellDate.setHours(0, 0, 0, 0);
        
        const now = new Date();
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        return cellDate < yesterday;
    };

    const showFutureDateError = () => {
        setShowSnackbar({
            show: true,
            message: <Translate id="hospital.treatment-inpatient-showValidationError-future" />,
            severity: 'warning'
        });
    };

    const showPastDateError = () => {
        setShowSnackbar({
            show: true,
            message: <Translate id="hospital.treatment-inpatient-showValidationError-past" />,
            severity: 'warning'
        });
    };

    // Helper function to calculate smart menu position
    const calculateMenuPosition = (rect: DOMRect, menuWidth: number = 200, menuHeight: number = 100) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let x = rect.right + 5; // Default: position to the right
        let y = rect.top;
        
        // Check if menu would go off-screen to the right
        if (x + menuWidth > viewportWidth) {
            // Position to the left of the cell instead
            x = rect.left - menuWidth - 5;
            
            // If still off-screen to the left, position at the right edge of viewport
            if (x < 0) {
                x = viewportWidth - menuWidth - 10;
            }
        }
        
        // Check if menu would go off-screen at the bottom
        if (y + menuHeight > viewportHeight) {
            // Position above the cell instead
            y = rect.bottom - menuHeight;
            
            // If still off-screen at the top, position at the top of viewport
            if (y < 0) {
                y = 10;
            }
        }
        
        return { x, y };
    };

    const AdministrationTooltip: React.FC<{
        administeredBy?: string;
        createdAt?: string;
        reasonNotAdministered?: string;
        status?: "successful" | "not successful" | null;
        language?: string;
        translate: (id: string) => any;
        children: React.ReactElement;
    }> = ({ administeredBy, createdAt, reasonNotAdministered, status, language = 'en', translate, children }) => {
        const { researcher, loading } = useResearcher(administeredBy || '');
        
        const tooltipLines: React.ReactNode[] = [];
        
        if (administeredBy) {
            if (loading) {
                tooltipLines.push(
                    <div key="loading">
                        {translate('hospital.treatment-inpatient-loading').toString()}
                    </div>
                );
            } else if (researcher) {
                tooltipLines.push(
                    <div key="administered-by">
                        {status === 'not successful' 
                            ? translate('hospital.treatment-inpatient-not-administered-by').toString()
                            : translate('hospital.treatment-inpatient-administered-by').toString()
                        }: {researcherFullName(researcher)}
                    </div>
                );
            } else {
                tooltipLines.push(
                    <div key="not-found">
                        {translate('hospital.treatment-inpatient-administered-by').toString()}: {translate('hospital.treatment-inpatient-researcher-not-found')}
                    </div>
                );
            }
        }

        if (status === 'not successful' && reasonNotAdministered) {
            tooltipLines.push(
                <div key="reason">
                    {translate('hospital.treatment-inpatient-reason').toString()}: {reasonNotAdministered}
                </div>
            );
        }
        
        if (createdAt) {
            const formattedDate = dateAndTimeFromPostgresString(language, createdAt);
            tooltipLines.push(
                <div key="date">
                    {translate('hospital.treatment-inpatient-date').toString()}: {formattedDate}
                </div>
            );
        }
        
        if (tooltipLines.length === 0) {
            return children;
        }
        
        return (
            <Tooltip 
                title={
                    <Box>
                        {tooltipLines}
                    </Box>
                }
                arrow
                placement="top"
            >
                {children}
            </Tooltip>
        );
    };

  
    // Helper to render the status cell
    const renderStatusCell = (status: "successful" | "not successful" | null, drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, administeredBy?: string, createdAt?: string, reasonNotAdministered?: string) => {
        let content;
        let backgroundColor = 'white';
        let color = 'black';
      
        switch (status) {
            case 'successful':
                content = <CheckIcon sx={{ fontSize: 16, color: 'green' }} />;
                break;
            case 'not successful':
                content = <CloseIcon sx={{ fontSize: 16, color: 'red' }} />;
                break;
            default:
                content = null;
                backgroundColor = '#e0e0e0';
                break;
        }
      
        const cellContent = (
            <Box
                sx={{
                    width: '100%',
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #ccc',
                    backgroundColor: backgroundColor,
                    color: color,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: '#f0f0f0'
                    }
                }}
                onContextMenu={(e) => {
                    e.preventDefault();

                    if (!isDateEditable(dateKey)) {

                        if (isFutureDate(dateKey)) {
                            showFutureDateError();
                        } 
                        else if (isPastDate(dateKey)) {
                            showPastDateError();
                        }
                        return;
                    }

                    setContextMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName });  // Store dateKey in day field
                    const rect = e.currentTarget.getBoundingClientRect();
                    const position = calculateMenuPosition(rect, 120, 80);
                    setContextMenuPosition(position);
                    setContextMenuOpen(true);
                }}
                onClick={(e) => {
                    if (!isDateEditable(dateKey)) {

                        if (isFutureDate(dateKey)) {
                            showFutureDateError();
                        } 
                        else if (isPastDate(dateKey)) {
                            showPastDateError();
                        }
                        return;

                    }

                    if (status === 'not successful' || status === 'successful') {
                        // For non-empty cells, show action menu
                        setCellActionMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName, currentStatus: status });
                        const rect = e.currentTarget.getBoundingClientRect();
                        const position = calculateMenuPosition(rect, 200, 100);
                        setCellActionMenuPosition(position);
                        setCellActionMenuOpen(true);
                    } 
                    
                    else {
                        // For empty cells, show context menu
                        setContextMenuCell({ drugIndex, day: dateKey, timeSlot: timeSlotName });

                        const rect = e.currentTarget.getBoundingClientRect();
                        const position = calculateMenuPosition(rect, 120, 80);
                        setContextMenuPosition(position);
                        setContextMenuOpen(true);
                    }
                }}
            >
                {content}
            </Box>
        );

        // Wrap with tooltip only if there's data to show
        if ((status === 'successful' || status === 'not successful') && (administeredBy || createdAt || reasonNotAdministered)) {
            return (
                <AdministrationTooltip 
                    administeredBy={administeredBy}
                    createdAt={createdAt}
                    reasonNotAdministered={reasonNotAdministered}
                    status={status}
                    language={language}
                    translate={translate} 
                >
                    {cellContent}
                </AdministrationTooltip>
            );

        }

        return cellContent;

    };

    const updateStatusInData = (drugIndex: number, dateKey: string, timeSlotName: TimeSlotName, status: "successful" | "not successful" | null, reasonNotAdministered?: string) => {
        if (onStatusUpdate) {
            onStatusUpdate(drugIndex, dateKey, timeSlotName, status, reasonNotAdministered);
        }
    };

    const handleContextMenuAction = (action: 'successful' | 'not successful') => {
        if (contextMenuCell) {

            if (action === 'not successful') {
                // For "not successful", open the reason popup instead of directly updating
                setSelectedCell({
                    drugIndex: contextMenuCell.drugIndex,
                    day: contextMenuCell.day,
                    timeSlot: contextMenuCell.timeSlot
                });
                setPopupOpen(true);
            } 
            
            else {
                // For "successful", directly update as before
                // Need to convert day number to dateKey here
                updateStatusInData(contextMenuCell.drugIndex, contextMenuCell.day, contextMenuCell.timeSlot, action);
            }
            
        }
        setContextMenuOpen(false);
        setContextMenuCell(null);
        setContextMenuPosition(null);
    };
  
    return (
        <Paper sx={{ width: '100%', overflowX: 'auto', boxSizing: 'border-box', position: 'relative' }}>

            {/* Month Header with Navigation */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                p: 2, 
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #ccc',
                gap: 1
            }}>
                <IconButton 
                    onClick={goToPreviousWeek}
                    size="small"
                    sx={{ 
                        opacity: isPreviousWeekDisabled() ? 0.5 : 1,
                        cursor: isPreviousWeekDisabled() ? 'not-allowed' : 'pointer'
                    }}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                            {currentMonth}
                        </Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                            {currentWeekInfo}
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={openWeekSelector}
                        size="small"
                        color="primary"
                        sx={{ 
                            '&:hover': { backgroundColor: 'rgba(63, 81, 181, 0.1)' }
                        }}
                    >
                        <CalendarMonthIcon />
                    </IconButton>
                </Box>
                
                <IconButton 
                    onClick={goToNextWeek}
                    size="small"
                    sx={{ 
                        opacity: isNextWeekDisabled() ? 0.5 : 1,
                        cursor: isNextWeekDisabled() ? 'not-allowed' : 'pointer'
                    }}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            {/* Week Selector Modal */}
            {weekSelectorOpen && (
                <Modal
                    open={weekSelectorOpen}
                    title={<Translate id="hospital.treatment-inpatient-select-week" />}
                    closeModal={() => setWeekSelectorOpen(false)}
                    maxWidth="sm"
                >
                    <Box sx={{ p: 2, minWidth: 400 }}>
                        <Grid container spacing={2}>
                            {/* Year Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                <InputLabel>
                                        <Translate id="hospital.treatment-inpatient-year" />
                                    </InputLabel>
                                    <Select
                                        value={selectedYear}
                                        label={translate('hospital.treatment-inpatient-year')}
                                        onChange={(e) => {
                                            const newYear = Number(e.target.value);
                                            setSelectedYear(newYear);
                                            setSelectedWeekIndex(0);
                                            
                                            // Update month to first valid month in new year
                                            const filteredMonths = getFilteredMonths(newYear);
                                            if (filteredMonths.length > 0) {
                                                setSelectedMonth(filteredMonths[0]);
                                            }
                                        }}
                                    >
                                        {getFilteredYears().map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Month Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                <InputLabel>
                                        <Translate id="hospital.treatment-inpatient-month" />
                                    </InputLabel>
                                    <Select
                                        value={selectedMonth}
                                        label={translate('hospital.treatment-inpatient-month')}
                                        onChange={(e) => {
                                            const newMonth = Number(e.target.value);
                                            setSelectedMonth(newMonth);
                                            setSelectedWeekIndex(0);
                                        }}
                                    >
                                        {getFilteredMonths(selectedYear).map((monthIndex) => {
                                            // Map language prop to locale code
                                            const locale = language === 'es' ? 'es' : language === 'fr' ? 'fr' : 'en';
                                            const monthName = new Date(2000, monthIndex, 1).toLocaleString(locale, { month: 'long' });
                                            return (
                                                <MenuItem key={monthIndex} value={monthIndex}>
                                                    {monthName}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Week Selector */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                <InputLabel>
                                        <Translate id="hospital.treatment-inpatient-week" />
                                    </InputLabel>
                                    <Select
                                        value={selectedWeekIndex}
                                        label={translate('hospital.treatment-inpatient-week')}
                                        onChange={(e) => setSelectedWeekIndex(Number(e.target.value))}
                                    >
                                        {getFilteredWeeks(selectedYear, selectedMonth).map((weekStart, index) => {
                                            const weekRange = formatWeekRange(weekStart, locale); // Pass locale here
                                            const weekLabel = `${translate('hospital.treatment-inpatient-week')} ${index + 1}: ${weekRange}`;
                                            return (
                                                <MenuItem 
                                                    key={index} 
                                                    value={index}
                                                >
                                                    {weekLabel}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Apply Button */}
                            <Grid item xs={12}>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    onClick={applyWeekSelection}
                                    sx={{ 
                                        mt: 2
                                    }}
                                >
                                    <Translate id="hospital.treatment-inpatient-apply" />
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Modal>
            )}
            {filteredData.length === 0 ? (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        p: 4,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5',
                        mt: 2
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        <Translate id="hospital.treatment-inpatient-no-treatments" />
                    </Typography>
                </Box>
            ) : (
                filteredData.map((drugData, filteredIndex) => {
                    // Find the original index in the data array for onStatusUpdate. This is done beccause the parent component (TreatmentInpatient.tsx) uses drugIndex to access the original array (const treatment = chartData[drugIndex];)
                    const originalIndex = data.findIndex(t => 
                        t.drug === drugData.drug && 
                        t.route === drugData.route && 
                        t.frequency === drugData.frequency &&
                        t.treatmentPostId === drugData.treatmentPostId
                    );
                    const drugIndex = originalIndex >= 0 ? originalIndex : filteredIndex;
                    
                    const isStopped = drugData.stopped === "1";
                    
                    return (
                        <Grid container key={drugIndex} wrap="nowrap" sx={{ mb: 2, position: 'relative', width: '100%' }}>
                            {/* Drug Information Column - existing code */}
                            <Grid item>
                                <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, backgroundColor: '#f0f0f0' }}>
                                <Typography variant="subtitle2">
                                        {translate('hospital.treatment-inpatient-drug')}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, backgroundColor: '#3f51b5', color: 'white', mt: -1 }}>
                                    <Typography variant="body2">{drugData.drug}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, mt: -1 }}>
                                    <Box sx={{ flex: 1, border: '1px solid #ccc', borderRight: 'none', p: 1 }}>
                                        <Typography variant="caption">Q: {drugData.doseAmount || "N/A"}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, border: '1px solid #ccc', p: 1 }}>
                                        <Typography variant="caption">U: {drugData.doseUnit || "N/A"}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, mt: -1 }}>
                                <Typography variant="caption">
                                        F: {translateWithFallback(`hospital.frecuency-options.${drugData.frequency}`, drugData.frequency)}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: { xs: 150, sm: 180, md: 200 }, minWidth: { xs: 150, sm: 180, md: 200 }, border: '1px solid #ccc', p: 1, mt: -1 }}>
                                <Typography variant="caption">
                                        R: {translateWithFallback(`hospital.route-options.${drugData.route.toLowerCase()}`, drugData.route)}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Time Slots and Daily Grid */}
                            <Grid item sx={{ flex: 1, minWidth: 0 }}>
                                <Grid container direction="column" wrap="nowrap" sx={{ width: '100%' }}>
                                    {/* Days Header - Updated to show actual dates */}
                                    <Grid item>
                                        <Grid container wrap="nowrap" sx={{ width: '100%' }}>
                                            <Box sx={{ width: 80, minWidth: 80, border: '1px solid #ccc', p: 1, backgroundColor: '#f0f0f0' }}>
                                            <Typography variant="subtitle2">
                                                    <Translate id="hospital.treatment-inpatient-time" />
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flex: 1 }}>
                                                {weekDays.map((day, i) => {
                                                    const isToday = day.toDateString() === new Date().toDateString();
                                                    return (
                                                        <Box
                                                            key={`day-header-${i}`}
                                                            sx={{
                                                                flex: '1 1 0',
                                                                minWidth: 24,
                                                                height: 24,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                border: '1px solid #ccc',
                                                                borderLeft: i === 0 ? '1px solid #ccc' : 'none',
                                                                backgroundColor: isToday ? '#4caf50' : '#e0e0e0',
                                                                color: isToday ? 'white' : 'inherit',
                                                                fontWeight: isToday ? 'bold' : 'normal',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <Typography variant="caption">{day.getDate()}</Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Administration Rows - dynamic per-drug time slots */}
                                    {(() => {
                                        const { names: drugSlotNames, labels: drugSlotLabels, isHourBased } = getTimeSlotsForFrequency(drugData.frequency, translate);
                                        const isCompact = drugSlotNames.length > 6; // Q2H has 12 slots
                                        return drugSlotLabels.map((label, timeSlotIndex) => (
                                            <Grid item key={`time-slot-row-${drugSlotNames[timeSlotIndex]}`}>
                                                <Grid container wrap="nowrap" sx={{ width: '100%' }}>
                                                    <Box sx={{ width: 80, minWidth: 80, border: '1px solid #ccc', borderTop: 'none', p: isCompact ? 0.5 : 1 }}>
                                                        <Typography variant="caption" sx={{ fontSize: isCompact ? 10 : undefined }}>{label}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flex: 1 }}>
                                                        {weekDays.map((day, dayIndex) => {
                                                            const dateKey = formatDateToISO(day);
                                                            const isDayValid = isDayValidForTreatment(day, drugData);
                                                            const isDayActive = isDayActiveForFrequency(drugData.frequency, day, drugData.startDate);

                                                            // If day is out of treatment date range, render empty cell
                                                            if (!isDayValid) {
                                                                return (
                                                                    <Box key={`cell-${dateKey}-${timeSlotIndex}`} sx={{ flex: '1 1 0', minWidth: 24 }}>
                                                                        {/* Empty cell - day is out of treatment date range */}
                                                                    </Box>
                                                                );
                                                            }

                                                            // For Q48H/Q72H: gray out non-applicable days
                                                            if (!isDayActive) {
                                                                return (
                                                                    <Box key={`cell-${dateKey}-${timeSlotIndex}`} sx={{
                                                                        flex: '1 1 0',
                                                                        minWidth: 24,
                                                                        height: isCompact ? 20 : 24,
                                                                        border: '1px solid #ccc',
                                                                        borderLeft: dayIndex === 0 ? '1px solid #ccc' : 'none',
                                                                        borderTop: 'none',
                                                                        backgroundColor: '#d0d0d0',
                                                                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 4px)',
                                                                    }}>
                                                                        {/* Grayed out - not an applicable day for this frequency */}
                                                                    </Box>
                                                                );
                                                            }

                                                            const administrationForDay = drugData.administrations?.[dateKey];
                                                            const timeSlotName: TimeSlotName = drugSlotNames[timeSlotIndex];
                                                            const status = administrationForDay?.timeSlots?.[timeSlotName]?.status || null;
                                                            const administeredBy = administrationForDay?.timeSlots?.[timeSlotName]?.administeredBy;
                                                            const createdAt = administrationForDay?.timeSlots?.[timeSlotName]?.createdAt;
                                                            const reasonNotAdministered = administrationForDay?.timeSlots?.[timeSlotName]?.reasonNotAdministered;

                                                            return (
                                                                <Box key={`cell-${dateKey}-${timeSlotIndex}`} sx={{ flex: '1 1 0', minWidth: 24 }}>
                                                                    {renderStatusCell(status, drugIndex, dateKey, timeSlotName, administeredBy, createdAt, reasonNotAdministered)}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        ));
                                    })()}
                                </Grid>
                            </Grid>

                            {/* Red Opaque Overlay for Stopped Treatments - existing code */}
                            {isStopped && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(244, 67, 54, 0.3)',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                        borderRadius: '4px'
                                    }}
                                />
                            )}
                        </Grid>
                    );
                })
            )}

            {/* Popup Modal for Reason Selection */}
            {popupOpen && (
                <Modal 
                    open={popupOpen}
                    title={<Translate id="hospital.treatment-inpatient-reason-modal-title" />}
                    closeModal={() => setPopupOpen(false)}
                    maxWidth="sm"
                >
                <Box sx={{ p: 2 }}>
                    {notAdministeringReasons.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        }
                        }}
                        onClick={() => {
                            setSelectedReason(item.code);

                            if (selectedCell) {
                                updateStatusInData(
                                    selectedCell.drugIndex, 
                                    selectedCell.day,
                                    selectedCell.timeSlot, 
                                    'not successful',  // Set status to not successful
                                    item.reasonNotAdministered
                                );
                            }

                            setPopupOpen(false);
                            setSelectedCell(null);  // Clear selected cell
                            setSelectedReason('');
                        }}
                    >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.reasonNotAdministered}
                        </Typography>
                        <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: selectedReason === item.code ? '2px solid #1976d2' : '2px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: selectedReason === item.code ? '#1976d2' : 'transparent',
                            color: selectedReason === item.code ? 'white' : '#333',
                            fontWeight: 'bold'
                        }}
                        >
                        {item.code}
                        </Box>
                    </Box>
                    ))}
                </Box>
                </Modal>
            )}

            {(contextMenuOpen || cellActionMenuOpen) && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1299, // Just below the menu (which has zIndex 1300)
                            backgroundColor: 'transparent'
                        }}
                        onClick={() => {
                            if (contextMenuOpen) {
                                setContextMenuOpen(false);
                                setContextMenuCell(null);
                                setContextMenuPosition(null);
                            }
                            if (cellActionMenuOpen) {
                                setCellActionMenuOpen(false);
                                setCellActionMenuCell(null);
                                setCellActionMenuPosition(null);
                            }
                        }}
                    />
                </Portal>    
            )}

            {/* Context Menu for Empty Cells */}
            {contextMenuOpen && contextMenuPosition && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: contextMenuPosition.y,
                            left: contextMenuPosition.x,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1300,
                            minWidth: 120
                        }}
                        onMouseLeave={() => {
                            setContextMenuOpen(false);
                            setContextMenuCell(null);
                            setContextMenuPosition(null);
                        }}
                    >
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => handleContextMenuAction('successful')}
                        >
                            <CheckIcon sx={{ fontSize: 16, color: 'green' }} />
                            <Typography variant="body2"><Translate id="hospital.treatment-inpatient-mark-as-successful" /></Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => handleContextMenuAction('not successful')}
                        >
                            <CloseIcon sx={{ fontSize: 16, color: 'red' }} />
                            <Typography variant="body2"><Translate id="hospital.treatment-inpatient-mark-as-not-successful" /></Typography>
                        </Box>
                    </Box>

                </Portal>
            )}

            {/* Cell Action Menu for Non-Empty Cells. It only appears when menu [should be visible] & [we know where to position it] & [we know which cell it's for]*/}
            {cellActionMenuOpen && cellActionMenuPosition && cellActionMenuCell && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: cellActionMenuPosition.y,
                            left: cellActionMenuPosition.x,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1300,
                            minWidth: 200
                        }}
                        onMouseLeave={() => {
                            setCellActionMenuOpen(false);
                            setCellActionMenuCell(null);
                            setCellActionMenuPosition(null);
                        }}
                    >
                        <Box
                            sx={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                            onClick={() => {
                                if (cellActionMenuCell) {
                                    updateStatusInData(
                                        cellActionMenuCell.drugIndex,
                                        cellActionMenuCell.day,
                                        cellActionMenuCell.timeSlot,
                                        null
                                    );
                                }
                                setCellActionMenuOpen(false);
                                setCellActionMenuCell(null);
                                setCellActionMenuPosition(null);
                            }}
                        >

                            <Typography variant="body2"><Translate id="hospital.treatment-inpatient-remove-content-from-cell" /></Typography>

                        </Box>
                    </Box>
                </Portal>
            )}

            {/* Snackbar for validation errors */}
            <Snackbar 
                open={showSnackbar.show} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity || 'warning'} 
                    sx={{ width: '100%' }}
                >
                    {showSnackbar.message}
                </Alert>
            </Snackbar>

        </Paper>
    );
};

export default withLocalize(TreatmentInpatientChartView);